import { useEffect, useState } from "react";
import { StudentLayout } from "@/components/Layouts";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { RecordingCard } from "@/components/RecordingCard";
import { ModuleCard } from "@/components/ModuleCard";
import { NoteCard } from "@/components/NoteCard";
import { EmptyState } from "@/components/EmptyState";
import { Video, BookOpen, FileText } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function StudentCourse() {
  const { refreshUser } = useAuth();
  const [recordings, setRecordings] = useState([]);
  const [modules, setModules] = useState([]);
  const [notes, setNotes] = useState([]);

  const load = async () => {
    const [r, m, n] = await Promise.all([
      api.get("/recordings").then((res) => res.data).catch(() => []),
      api.get("/modules").then((res) => res.data).catch(() => []),
      api.get("/notes").then((res) => res.data).catch(() => []),
    ]);
    setRecordings(r);
    setModules(m);
    setNotes(n);
  };

  useEffect(() => { load(); }, []);

  const handleToggleComplete = async (mod) => {
    try {
      if (mod.completed) {
        await api.post(`/modules/${mod.id}/uncomplete`);
        toast.success("Module marked as not completed.");
      } else {
        await api.post(`/modules/${mod.id}/complete`);
        toast.success("Module marked as completed.");
      }
      await load();
      await refreshUser();
    } catch {
      toast.error("Gagal kemas kini status modul.");
    }
  };

  return (
    <StudentLayout title="Beginner Stock Investment Class">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight">Beginner Stock Investment Class</h1>
          <p className="mt-2 text-muted-foreground">
            Access recordings, learning modules, and notes for this course.
          </p>
        </div>

        <Tabs defaultValue="rakaman" className="w-full">
          <TabsList className="w-full sm:w-auto" data-testid="course-tabs">
            <TabsTrigger value="rakaman" data-testid="tab-rakaman">Class Recordings</TabsTrigger>
            <TabsTrigger value="modul" data-testid="tab-modul">Learning Modules</TabsTrigger>
            <TabsTrigger value="nota" data-testid="tab-nota">Class Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="rakaman" className="mt-6">
            <div className="mb-4">
              <h2 className="text-xl font-heading font-semibold">Class Recordings</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Re-watch every class session recording. Recordings are uploaded by admin after class ends.
              </p>
            </div>
            {recordings.length === 0 ? (
              <EmptyState icon={Video} title="No recordings yet" description="Recordings have not been uploaded yet. Please check again after class ends." />
            ) : (
              <div className="grid sm:grid-cols-2 gap-4">
                {recordings.map((r) => <RecordingCard key={r.id} recording={r} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="modul" className="mt-6">
            <div className="mb-4">
              <h2 className="text-xl font-heading font-semibold">Learning Modules</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Follow the structured stock learning modules in the order provided.
              </p>
            </div>
            {modules.length === 0 ? (
              <EmptyState icon={BookOpen} title="No modules yet" description="This module's content is not yet available. Please check again later." />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {modules.map((m) => <ModuleCard key={m.id} module={m} onToggleComplete={handleToggleComplete} />)}
              </div>
            )}
          </TabsContent>

          <TabsContent value="nota" className="mt-6">
            <div className="mb-4">
              <h2 className="text-xl font-heading font-semibold">Class Notes</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Download class notes by available learning module.
              </p>
            </div>
            {notes.length === 0 ? (
              <EmptyState icon={FileText} title="No notes yet" description="Notes are not yet available for download." />
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {notes.map((n) => <NoteCard key={n.id} note={n} />)}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </StudentLayout>
  );
}
